<template>
  <div>
    <div class="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-4 sm:mb-6">
      <h1 class="text-2xl sm:text-3xl font-bold">{{ isEdit ? 'แก้ไขสินค้า' : 'สร้างสินค้าใหม่' }}</h1>
      <router-link to="/admin/products" class="text-gray-600 hover:text-gray-900 text-sm sm:text-base">
        &larr; กลับไปหน้ารายการสินค้า
      </router-link>
    </div>

    <div class="bg-white rounded-lg shadow p-4 sm:p-6 max-w-4xl mx-auto">
      <div v-if="loading" class="flex justify-center py-12">
        Loading...
      </div>
      <Form v-else @submit="saveProduct" :validation-schema="schema" :initial-values="form" v-slot="{ errors, isSubmitting }">
        
        <!-- Basic Info -->
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 mb-4 sm:mb-6">
            <div>
                <label class="block text-gray-700 mb-1 text-sm sm:text-base">ชื่อสินค้า</label>
                <Field 
                    name="name" 
                    type="text" 
                    class="w-full border rounded px-3 sm:px-4 py-2 text-sm sm:text-base"
                    :class="{ 'border-red-500': errors.name }"
                />
                <ErrorMessage name="name" class="text-red-500 text-xs sm:text-sm mt-1" />
            </div>
            <div>
                <label class="block text-gray-700 mb-1 text-sm sm:text-base">หมวดหมู่</label>
                <Field 
                    name="category_id" 
                    as="select" 
                    class="w-full border rounded px-3 sm:px-4 py-2 text-sm sm:text-base"
                    :class="{ 'border-red-500': errors.category_id }"
                >
                    <option value="" disabled>เลือกหมวดหมู่</option>
                    <option v-for="cat in categories" :key="cat.id" :value="cat.id">{{ cat.name }}</option>
                </Field>
                <ErrorMessage name="category_id" class="text-red-500 text-xs sm:text-sm mt-1" />
            </div>
        </div>

        <div class="mb-4 sm:mb-6">
            <label class="block text-gray-700 mb-1 text-sm sm:text-base">รายละเอียด</label>
            <Field 
                name="description" 
                as="textarea" 
                rows="4" 
                class="w-full border rounded px-3 sm:px-4 py-2 text-sm sm:text-base"
                :class="{ 'border-red-500': errors.description }"
            />
            <ErrorMessage name="description" class="text-red-500 text-xs sm:text-sm mt-1" />
        </div>

        <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6 mb-4 sm:mb-6">
            <div>
                <label class="block text-gray-700 mb-1 text-sm sm:text-base">ราคา (บาท)</label>
                <Field 
                    name="price" 
                    type="number" 
                    class="w-full border rounded px-3 sm:px-4 py-2 text-sm sm:text-base"
                    :class="{ 'border-red-500': errors.price }"
                />
                <ErrorMessage name="price" class="text-red-500 text-xs sm:text-sm mt-1" />
            </div>
            <div>
                <label class="block text-gray-700 mb-1 text-sm sm:text-base">จำนวนสินค้า</label>
                <template v-if="hasVariants">
                    <input 
                        type="number" 
                        :value="totalVariantStock"
                        disabled
                        class="w-full border rounded px-3 sm:px-4 py-2 text-sm sm:text-base bg-gray-100 cursor-not-allowed"
                    />
                    <p class="text-xs text-gray-500 mt-1">คำนวณจากตัวเลือกอัตโนมัติ</p>
                </template>
                <template v-else>
                    <Field 
                        name="stock_quantity" 
                        type="number" 
                        class="w-full border rounded px-3 sm:px-4 py-2 text-sm sm:text-base"
                        :class="{ 'border-red-500': errors.stock_quantity }"
                    />
                    <ErrorMessage name="stock_quantity" class="text-red-500 text-xs sm:text-sm mt-1" />
                </template>
            </div>
            <div class="flex items-center sm:mt-6">
                <Field 
                    name="is_active" 
                    type="checkbox" 
                    :value="true" 
                    :unchecked-value="false"
                    class="mr-2 h-5 w-5" 
                />
                <label class="text-gray-700 text-sm sm:text-base">เปิดใช้งาน (แสดงสินค้า)</label>
            </div>
        </div>

        <!-- Variants Toggle -->
        <div class="border-t pt-4">
            <div class="flex items-center mb-4">
                <Field 
                    name="has_variants" 
                    type="checkbox" 
                    :value="true" 
                    :unchecked-value="false"
                    class="mr-2 h-5 w-5" 
                    v-model="hasVariants" 
                />
                <label class="font-bold text-base sm:text-lg">มีตัวเลือกสินค้า (สี/ไซส์)?</label>
            </div>

            <div v-if="hasVariants" class="bg-gray-50 p-3 sm:p-4 rounded">
                <p class="text-sm text-gray-600 mb-3">เพิ่มตัวเลือกสี และ/หรือ ไซส์ พร้อมจำนวนสินค้าแต่ละตัวเลือก</p>
                
                <div v-for="(variant, index) in variants" :key="index" class="flex flex-col sm:flex-row sm:items-end gap-2 sm:gap-4 mb-4 pb-4 border-b sm:border-0 sm:pb-0 sm:mb-3">
                    <div class="flex-1">
                        <label class="block text-xs text-gray-500 mb-1">สี</label>
                        <input v-model="variant.color" type="text" placeholder="เช่น แดง, น้ำเงิน" class="border rounded px-2 py-1 w-full text-sm">
                    </div>
                    <div class="flex-1">
                        <label class="block text-xs text-gray-500 mb-1">ไซส์</label>
                        <input v-model="variant.size" type="text" placeholder="เช่น S, M, L, XL" class="border rounded px-2 py-1 w-full text-sm">
                    </div>
                    <div class="flex-1">
                        <label class="block text-xs text-gray-500 mb-1">จำนวน</label>
                        <input v-model.number="variant.stock_quantity" type="number" min="0" class="border rounded px-2 py-1 w-full text-sm">
                    </div>
                    <button @click="removeVariant(index)" type="button" class="text-red-500 hover:text-red-700 text-sm py-1 sm:pb-1">ลบ</button>
                </div>
                
                <div class="flex justify-between items-center mt-3">
                    <button @click="addVariant" type="button" class="text-primary hover:text-primary-dark text-sm font-bold">+ เพิ่มตัวเลือก</button>
                    <span class="text-sm text-gray-600">รวม: <strong>{{ totalVariantStock }}</strong> ชิ้น</span>
                </div>
            </div>
        </div>

        <!-- Image Upload -->
        <div class="border-t pt-4">
             <label class="block font-bold text-base sm:text-lg mb-2">รูปสินค้า</label>
             <div class="flex items-center">
                 <input type="file" multiple @change="handleImageUpload" accept="image/*" class="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-cream-dark file:text-primary hover:file:bg-cream" ref="fileInput">
             </div>
             
             <!-- New Images Preview -->
             <div v-if="newImagePreviews.length > 0" class="mt-4">
                 <p class="text-sm text-gray-600 mb-2">รูปภาพใหม่ที่จะอัปโหลด</p>
                 <div class="flex flex-wrap gap-2 sm:gap-4">
                     <div v-for="(preview, index) in newImagePreviews" :key="index" class="relative group">
                         <img 
                             :src="preview.url" 
                             class="w-16 h-16 sm:w-20 sm:h-20 object-cover rounded border-2 border-dashed border-green-400"
                         >
                         <div class="absolute top-0 left-0 bg-green-500 text-white text-xs px-1 py-0.5 rounded-br rounded-tl">
                             ใหม่
                         </div>
                         <button 
                             @click="removeNewImage(index)"
                             type="button"
                             class="absolute top-0 right-0 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
                             title="ยกเลิก"
                         >
                             ×
                         </button>
                     </div>
                 </div>
             </div>
             
             <!-- Existing Images -->
             <div v-if="existingImages.length > 0" class="mt-4">
                 <p class="text-sm text-gray-600 mb-2">รูปภาพที่อัปโหลดแล้ว (คลิกเลือกรูปปก)</p>
                 <div class="flex flex-wrap gap-2 sm:gap-4">
                     <div v-for="(img, index) in existingImages" :key="img.id" class="relative group">
                         <img 
                             :src="getImageUrl(img.image_path)" 
                             @click="setCoverImage(img.id, index)"
                             :class="[
                                 'w-16 h-16 sm:w-20 sm:h-20 object-cover rounded cursor-pointer transition-all',
                                 img.sort_order === 1 ? 'ring-2 ring-primary ring-offset-2' : 'hover:opacity-80'
                             ]"
                         >
                         <!-- Cover badge -->
                         <div v-if="img.sort_order === 1" class="absolute top-0 left-0 bg-primary text-white text-xs px-1 py-0.5 rounded-br rounded-tl">
                             ปก
                         </div>
                         <!-- Delete button -->
                         <button 
                             @click="deleteImage(img.id, index)"
                             type="button"
                             class="absolute top-0 right-0 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
                             title="ลบรูปภาพ"
                         >
                             ×
                         </button>
                     </div>
                 </div>
             </div>
        </div>

        <div class="flex flex-col sm:flex-row justify-end gap-3 sm:gap-4 pt-6 border-t">
            <router-link to="/admin/products" class="px-6 py-2 border border-primary-300 rounded text-primary-600 hover:bg-primary-50 font-medium text-center text-sm sm:text-base order-2 sm:order-1">ยกเลิก</router-link>
            <button type="submit" class="px-6 py-2 bg-primary text-white rounded hover:bg-primary-dark font-bold text-sm sm:text-base order-1 sm:order-2" :disabled="isSubmitting">
                {{ isSubmitting ? 'กำลังบันทึก...' : (isEdit ? 'อัปเดตสินค้า' : 'สร้างสินค้า') }}
            </button>
        </div>

      </Form>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import api from '../../api';
import { Form, Field, ErrorMessage } from 'vee-validate';
import * as yup from 'yup';
import { useNotification } from '../../composables/useNotification';
import { getImageUrl } from '../../utils/imageUrl';

const route = useRoute();
const router = useRouter();
const { showSuccess, showError, showWarning, handleApiError, confirmAction } = useNotification();

const isEdit = computed(() => !!route.params.id);
const loading = ref(false);
const categories = ref([]);
const existingImages = ref([]);
const newImages = ref([]);
const newImagePreviews = ref([]);
const fileInput = ref(null);

// Separate variant state since we are not using VeeValidate array fields for simplicity in this MVP
const hasVariants = ref(false);
const variants = ref([]);

// คำนวณจำนวนสินค้ารวมจากตัวเลือกทั้งหมด
const totalVariantStock = computed(() => {
    return variants.value.reduce((sum, v) => sum + (parseInt(v.stock_quantity) || 0), 0);
});

const form = ref({
    name: '',
    category_id: '',
    description: '',
    price: 0,
    stock_quantity: 0,
    is_active: true,
    has_variants: false
});

const schema = yup.object({
    name: yup.string().required('กรุณากรอกชื่อสินค้า'),
    category_id: yup.string().required('กรุณาเลือกหมวดหมู่'),
    price: yup.number().required('กรุณากรอกราคา').min(0, 'ราคาต้องไม่ติดลบ'),
    stock_quantity: yup.number().min(0, 'จำนวนต้องไม่ติดลบ'),
    description: yup.string().nullable(),
    is_active: yup.boolean(),
    has_variants: yup.boolean()
});

const handleImageUpload = (event) => {
    const files = Array.from(event.target.files);
    
    files.forEach(file => {
        newImages.value.push(file);
        
        // Create preview URL using URL.createObjectURL (faster and more reliable)
        const previewUrl = URL.createObjectURL(file);
        newImagePreviews.value.push({
            url: previewUrl,
            name: file.name
        });
    });
    
    // Clear file input
    if (fileInput.value) {
        fileInput.value.value = '';
    }
};

const removeNewImage = (index) => {
    // Revoke the object URL to free memory
    if (newImagePreviews.value[index]?.url) {
        URL.revokeObjectURL(newImagePreviews.value[index].url);
    }
    newImages.value.splice(index, 1);
    newImagePreviews.value.splice(index, 1);
};

const deleteImage = async (imageId, index) => {
    const confirmed = await confirmAction(
        'ลบรูปภาพ?',
        'คุณต้องการลบรูปภาพนี้ใช่หรือไม่?',
        'ลบ',
        'ยกเลิก'
    );
    
    if (!confirmed) {
        return;
    }
    
    try {
        await api.delete(`/admin/product-images/${imageId}`);
        existingImages.value.splice(index, 1);
        showSuccess('ลบรูปภาพเรียบร้อยแล้ว');
        
        // If deleted image was cover, set first image as new cover
        if (existingImages.value.length > 0 && existingImages.value[0].sort_order !== 1) {
            await setCoverImage(existingImages.value[0].id, 0);
        }
    } catch (error) {
        handleApiError(error);
    }
};

const setCoverImage = async (imageId, index) => {
    // If already cover, do nothing
    if (existingImages.value[index].sort_order === 1) {
        return;
    }
    
    try {
        // Update sort orders: set selected image as 1, increment others
        const updates = existingImages.value.map((img, i) => {
            if (i === index) {
                return { ...img, sort_order: 1 };
            } else if (img.sort_order === 1) {
                // Previous cover becomes second
                return { ...img, sort_order: 2 };
            } else if (img.sort_order > 1) {
                // Others shift down
                return { ...img, sort_order: img.sort_order + 1 };
            }
            return img;
        });
        
        // Update backend
        for (const update of updates) {
            if (update.sort_order) {
                await api.put(`/admin/product-images/${update.id}`, { sort_order: update.sort_order });
            }
        }
        
        // Update local state
        existingImages.value = updates;
        showSuccess('เปลี่ยนรูปปกเรียบร้อยแล้ว');
    } catch (error) {
        handleApiError(error);
    }
};

const addVariant = () => {
    variants.value.push({ color: '', size: '', stock_quantity: 0 });
};

const removeVariant = (index) => {
    variants.value.splice(index, 1);
};

const saveProduct = async (values, { setErrors }) => {
    // ถ้ามีตัวเลือก ให้ใช้จำนวนรวมจากตัวเลือก
    const stockQty = hasVariants.value ? totalVariantStock.value : values.stock_quantity;
    
    // Merge variants into values
    const payload = {
        ...values,
        stock_quantity: stockQty,
        has_variants: hasVariants.value,
        variants: hasVariants.value ? variants.value.map(v => ({
            color: v.color || null,
            size: v.size || null,
            stock_quantity: parseInt(v.stock_quantity) || 0
        })) : []
    };

    try {
        let productId = route.params.id;
        
        // 1. Save Basic Info
        if (isEdit.value) {
            await api.put(`/admin/products/${productId}`, payload);
        } else {
            const res = await api.post('/admin/products', payload);
            productId = res.data.data.id;
        }
        
        // 2. Upload Images if any
        if (newImages.value.length > 0) {
            let uploadSuccess = 0;
            for (const file of newImages.value) {
                const formData = new FormData();
                formData.append('image', file);
                try {
                    await api.post(`/admin/products/${productId}/images`, formData, {
                        headers: { 'Content-Type': 'multipart/form-data' }
                    });
                    uploadSuccess++;
                } catch (err) {
                    showWarning(`ไม่สามารถอัปโหลดรูป ${file.name} ได้`);
                }
            }
            if (uploadSuccess > 0) {
                showSuccess(`อัปโหลดรูปภาพ ${uploadSuccess} รูปเรียบร้อยแล้ว`);
            }
            
            // Clear new images after upload
            newImages.value = [];
            newImagePreviews.value = [];
        }
        
        showSuccess(isEdit.value ? 'อัปเดตสินค้าเรียบร้อยแล้ว' : 'สร้างสินค้าเรียบร้อยแล้ว');
        router.push('/admin/products');
    } catch (error) {
        handleApiError(error, setErrors);
    }
};

onMounted(async () => {
    loading.value = true;
    try {
        const catRes = await api.get('/categories');
        categories.value = catRes.data.data;
    } catch (e) {
        showError('ไม่สามารถโหลดข้อมูลหมวดหมู่ได้');
    }

    if (isEdit.value) {
         try {
            const res = await api.get(`/admin/products/${route.params.id}`);
            const p = res.data.data;
            form.value = {
                name: p.name,
                category_id: p.category_id,
                description: p.description,
                price: p.price,
                stock_quantity: p.stock_quantity,
                is_active: Boolean(p.is_active),
                has_variants: Boolean(p.has_variants)
            };
            
            hasVariants.value = Boolean(p.has_variants);
            // แปลง variants ให้มีแค่ color, size, stock_quantity
            variants.value = (p.variants || []).map(v => ({
                color: v.color || '',
                size: v.size || '',
                stock_quantity: v.stock_quantity || 0
            }));
            existingImages.value = p.images || [];
         } catch (e) {
             showError('ไม่สามารถโหลดข้อมูลสินค้าได้');
             router.push('/admin/products');
         }
    }
    loading.value = false;
});
</script>
